import type { LegalDocument } from "../legal/types"

/**
 * Bilingual (English / Arabic) content for the Terms & Conditions page.
 * Governing law: State of Qatar. Contact and legal-entity details are left as
 * clearly-marked placeholders to be finalised by legal counsel.
 */
export const termsContent: LegalDocument = {
  title: {
    en: "Terms & Conditions",
    ar: "الشروط والأحكام",
  },
  lastUpdated: {
    en: "3 September 2026",
    ar: "٣ سبتمبر ٢٠٢٦",
  },
  intro: {
    en: "These Terms & Conditions (the “Terms”) govern your access to and use of the Taqat AI Employee Store platform, websites, and related services (together, the “Service”). Please read them carefully. By accessing or using the Service, you agree to be bound by these Terms.",
    ar: "تحكم هذه الشروط والأحكام (“الشروط”) وصولك إلى منصة “طاقة” لمتجر الموظفين المدعومين بالذكاء الاصطناعي ومواقعها الإلكترونية والخدمات المرتبطة بها (يُشار إليها مجتمعةً بـ“الخدمة”) واستخدامك لها. يُرجى قراءتها بعناية. إن وصولك إلى الخدمة أو استخدامك لها يعني موافقتك على الالتزام بهذه الشروط.",
  },
  sections: [
    {
      id: "acceptance",
      heading: { en: "Acceptance of the Terms", ar: "قبول الشروط" },
      body: [
        {
          type: "p",
          en: "By creating an account, accessing, or otherwise using the Service, you confirm that you have read, understood, and agree to be bound by these Terms and by our Privacy Policy. If you do not agree, you must not use the Service.",
          ar: "بإنشائك حساباً أو وصولك إلى الخدمة أو استخدامها بأي شكل، فإنك تقرّ بأنك قد قرأت هذه الشروط وسياسة الخصوصية الخاصة بنا وفهمتها وتوافق على الالتزام بها. وإذا كنت لا توافق عليها، فيجب عليك عدم استخدام الخدمة.",
        },
        {
          type: "p",
          en: "If you use the Service on behalf of an organisation, you represent that you are authorised to bind that organisation to these Terms, and “you” refers to that organisation.",
          ar: "إذا كنت تستخدم الخدمة نيابةً عن مؤسسة، فإنك تقرّ بأنك مخوّل بإلزام تلك المؤسسة بهذه الشروط، ويُقصد بكلمة “أنت” تلك المؤسسة.",
        },
      ],
    },
    {
      id: "definitions",
      heading: { en: "Definitions", ar: "التعريفات" },
      body: [
        {
          type: "list",
          en: [
            "“Company”, “we”, “us”, or “our” means the entity operating the Service, [Legal Entity Name], established in the State of Qatar.",
            "“AI Employee” or “Agent” means an artificial-intelligence assistant configured, deployed, or used through the Service.",
            "“User Content” means any data, prompts, files, or materials you submit to or generate through the Service.",
            "“Workspace” means the account environment in which your users, agents, and data are organised.",
          ],
          ar: [
            "“الشركة” أو “نحن” أو “نا” تعني الجهة المشغّلة للخدمة، [اسم الكيان القانوني]، المؤسَّسة في دولة قطر.",
            "“الموظف الذكي” أو “الوكيل” يعني مساعداً يعمل بالذكاء الاصطناعي يتم إعداده أو نشره أو استخدامه من خلال الخدمة.",
            "“محتوى المستخدم” يعني أي بيانات أو أوامر نصية أو ملفات أو مواد تقوم بتقديمها إلى الخدمة أو إنشائها من خلالها.",
            "“مساحة العمل” تعني بيئة الحساب التي يتم فيها تنظيم مستخدميك ووكلائك وبياناتك.",
          ],
        },
      ],
    },
    {
      id: "eligibility",
      heading: { en: "Eligibility & Accounts", ar: "الأهلية والحسابات" },
      body: [
        {
          type: "p",
          en: "You must be at least 18 years old and capable of forming a binding contract to use the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.",
          ar: "يجب أن يكون عمرك 18 عاماً على الأقل وأن تكون قادراً على إبرام عقد ملزم لاستخدام الخدمة. أنت مسؤول عن الحفاظ على سرية بيانات اعتماد حسابك وعن جميع الأنشطة التي تتم من خلال حسابك.",
        },
        {
          type: "p",
          en: "You agree to provide accurate and complete information and to keep it up to date. You must notify us promptly of any unauthorised use of your account.",
          ar: "توافق على تقديم معلومات دقيقة وكاملة والحفاظ على تحديثها. ويجب عليك إخطارنا فوراً بأي استخدام غير مصرّح به لحسابك.",
        },
      ],
    },
    {
      id: "service",
      heading: { en: "The Service", ar: "الخدمة" },
      body: [
        {
          type: "p",
          en: "The Service lets you discover, configure, deploy, and manage AI Employees that perform tasks, respond to messages, and integrate with third-party tools. Features may be added, changed, or removed over time.",
          ar: "تتيح لك الخدمة اكتشاف الموظفين الأذكياء وإعدادهم ونشرهم وإدارتهم بحيث يؤدّون المهام ويردّون على الرسائل ويتكاملون مع أدوات الطرف الثالث. وقد تتم إضافة الميزات أو تغييرها أو إزالتها مع مرور الوقت.",
        },
        {
          type: "p",
          en: "We may set technical limits (for example on usage, credits, storage, or request rates) and may modify these limits at our discretion.",
          ar: "قد نضع حدوداً تقنية (مثل حدود الاستخدام أو الأرصدة أو التخزين أو معدّلات الطلبات) ويجوز لنا تعديل هذه الحدود وفقاً لتقديرنا.",
        },
      ],
    },
    {
      id: "acceptable-use",
      heading: { en: "Acceptable Use", ar: "الاستخدام المقبول" },
      body: [
        {
          type: "p",
          en: "You agree not to misuse the Service. In particular, you must not:",
          ar: "توافق على عدم إساءة استخدام الخدمة. وعلى وجه الخصوص، يجب عليك عدم القيام بما يلي:",
        },
        {
          type: "list",
          en: [
            "Use the Service for any unlawful, harmful, fraudulent, or infringing purpose.",
            "Attempt to gain unauthorised access to the Service, other accounts, or our systems.",
            "Upload malware or interfere with the integrity or performance of the Service.",
            "Use the Service to generate or distribute content that is illegal, defamatory, or violates the rights of others.",
            "Reverse engineer, resell, or circumvent usage limits of the Service except as permitted by law.",
          ],
          ar: [
            "استخدام الخدمة لأي غرض غير قانوني أو ضار أو احتيالي أو مُنتهِك للحقوق.",
            "محاولة الوصول غير المصرّح به إلى الخدمة أو إلى حسابات أخرى أو إلى أنظمتنا.",
            "رفع البرمجيات الخبيثة أو التدخّل في سلامة الخدمة أو أدائها.",
            "استخدام الخدمة لإنشاء أو توزيع محتوى غير قانوني أو تشهيري أو ينتهك حقوق الآخرين.",
            "إجراء الهندسة العكسية للخدمة أو إعادة بيعها أو التحايل على حدود استخدامها إلا في الحدود التي يسمح بها القانون.",
          ],
        },
      ],
    },
    {
      id: "ai-output",
      heading: { en: "AI Output & Limitations", ar: "مخرجات الذكاء الاصطناعي وحدودها" },
      body: [
        {
          type: "p",
          en: "The Service relies on artificial-intelligence models that generate content automatically. AI output may be inaccurate, incomplete, or unsuitable for your particular purpose. You are responsible for reviewing outputs before relying on them, and you should not treat AI output as professional, legal, financial, or medical advice.",
          ar: "تعتمد الخدمة على نماذج الذكاء الاصطناعي التي تُنشئ المحتوى تلقائياً. وقد تكون مخرجات الذكاء الاصطناعي غير دقيقة أو غير كاملة أو غير مناسبة لغرضك المحدّد. وأنت مسؤول عن مراجعة المخرجات قبل الاعتماد عليها، ولا ينبغي اعتبار مخرجات الذكاء الاصطناعي مشورة مهنية أو قانونية أو مالية أو طبية.",
        },
        {
          type: "p",
          en: "You are solely responsible for the tasks you configure your AI Employees to perform and for any decisions taken on the basis of their output.",
          ar: "أنت وحدك المسؤول عن المهام التي تُهيّئ موظفيك الأذكياء لأدائها وعن أي قرارات تُتَّخذ بناءً على مخرجاتهم.",
        },
      ],
    },
    {
      id: "your-content",
      heading: { en: "Your Content & Data", ar: "محتواك وبياناتك" },
      body: [
        {
          type: "p",
          en: "You retain all ownership rights in your User Content. You grant us a limited licence to host, process, and transmit your User Content solely to provide and improve the Service, and to send it to the third-party AI providers necessary to fulfil your requests.",
          ar: "تحتفظ بجميع حقوق الملكية في محتوى المستخدم الخاص بك. وتمنحنا ترخيصاً محدوداً لاستضافة محتوى المستخدم الخاص بك ومعالجته ونقله لغرض تقديم الخدمة وتحسينها فقط، وإرساله إلى مزوّدي الذكاء الاصطناعي من الأطراف الثالثة اللازمين لتلبية طلباتك.",
        },
        {
          type: "p",
          en: "Our handling of personal data is described in the Privacy Policy. You are responsible for ensuring you have the necessary rights and consents for any data you submit.",
          ar: "تُوضَّح كيفية تعاملنا مع البيانات الشخصية في سياسة الخصوصية. وأنت مسؤول عن ضمان امتلاكك الحقوق والموافقات اللازمة لأي بيانات تقوم بتقديمها.",
        },
      ],
    },
    {
      id: "intellectual-property",
      heading: { en: "Intellectual Property", ar: "الملكية الفكرية" },
      body: [
        {
          type: "p",
          en: "The Service, including its software, design, trademarks, and content (excluding User Content), is owned by the Company or its licensors and is protected by intellectual-property laws. Except for the rights expressly granted to you, no rights are transferred to you.",
          ar: "الخدمة، بما في ذلك برمجياتها وتصميمها وعلاماتها التجارية ومحتواها (باستثناء محتوى المستخدم)، مملوكة للشركة أو للمرخِّصين لها ومحمية بموجب قوانين الملكية الفكرية. وباستثناء الحقوق الممنوحة لك صراحةً، لا تُنقَل إليك أي حقوق.",
        },
      ],
    },
    {
      id: "third-party",
      heading: { en: "Third-Party Integrations", ar: "تكاملات الأطراف الثالثة" },
      body: [
        {
          type: "p",
          en: "The Service may integrate with third-party services (such as messaging platforms, AI providers, and productivity tools). Your use of those services is governed by their own terms, and we are not responsible for their availability, content, or practices.",
          ar: "قد تتكامل الخدمة مع خدمات الأطراف الثالثة (مثل منصات المراسلة ومزوّدي الذكاء الاصطناعي وأدوات الإنتاجية). ويخضع استخدامك لتلك الخدمات لشروطها الخاصة، ولسنا مسؤولين عن توافرها أو محتواها أو ممارساتها.",
        },
      ],
    },
    {
      id: "fees",
      heading: { en: "Fees, Credits & Billing", ar: "الرسوم والأرصدة والفوترة" },
      body: [
        {
          type: "p",
          en: "Certain features require a paid subscription or the consumption of credits. Fees, billing cycles, and credit usage are described at the point of purchase. Unless required by law, fees are non-refundable. We may change pricing prospectively with reasonable notice.",
          ar: "تتطلب بعض الميزات اشتراكاً مدفوعاً أو استهلاك أرصدة. وتُوضَّح الرسوم ودورات الفوترة واستخدام الأرصدة عند الشراء. وما لم يقتضِ القانون خلاف ذلك، فإن الرسوم غير قابلة للاسترداد. ويجوز لنا تغيير الأسعار مستقبلاً بعد إشعار معقول.",
        },
        {
          type: "p",
          en: "You are responsible for any taxes associated with your use of the Service, except for taxes based on our net income.",
          ar: "أنت مسؤول عن أي ضرائب مرتبطة باستخدامك للخدمة، باستثناء الضرائب المفروضة على صافي دخلنا.",
        },
      ],
    },
    {
      id: "disclaimers",
      heading: { en: "Disclaimers", ar: "إخلاء المسؤولية" },
      body: [
        {
          type: "p",
          en: "The Service is provided “as is” and “as available” without warranties of any kind, whether express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or secure.",
          ar: "تُقدَّم الخدمة “كما هي” و“حسب توافرها” دون أي ضمانات من أي نوع، صريحة كانت أم ضمنية، بما في ذلك ضمانات القابلية للتسويق والملاءمة لغرض معيّن وعدم انتهاك حقوق الغير. ولا نضمن أن تكون الخدمة متواصلة أو خالية من الأخطاء أو آمنة.",
        },
      ],
    },
    {
      id: "liability",
      heading: { en: "Limitation of Liability", ar: "حدود المسؤولية" },
      body: [
        {
          type: "p",
          en: "To the maximum extent permitted by law, the Company will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, data, or goodwill, arising out of or related to your use of the Service. Our total aggregate liability will not exceed the amounts you paid to us for the Service in the twelve (12) months preceding the claim.",
          ar: "إلى أقصى حد يسمح به القانون، لن تكون الشركة مسؤولة عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية، أو عن أي خسارة في الأرباح أو البيانات أو السمعة التجارية، تنشأ عن استخدامك للخدمة أو تتعلق به. ولن تتجاوز مسؤوليتنا الإجمالية القصوى المبالغ التي دفعتها لنا مقابل الخدمة خلال الاثني عشر (12) شهراً السابقة للمطالبة.",
        },
      ],
    },
    {
      id: "indemnification",
      heading: { en: "Indemnification", ar: "التعويض" },
      body: [
        {
          type: "p",
          en: "You agree to indemnify and hold the Company harmless from any claims, damages, liabilities, and expenses arising out of your User Content, your use of the Service, or your breach of these Terms.",
          ar: "توافق على تعويض الشركة وإبراء ذمّتها من أي مطالبات أو أضرار أو التزامات أو نفقات تنشأ عن محتوى المستخدم الخاص بك أو استخدامك للخدمة أو إخلالك بهذه الشروط.",
        },
      ],
    },
    {
      id: "termination",
      heading: { en: "Suspension & Termination", ar: "التعليق والإنهاء" },
      body: [
        {
          type: "p",
          en: "We may suspend or terminate your access to the Service if you breach these Terms or if we are required to do so by law. You may stop using the Service at any time. Provisions that by their nature should survive termination will remain in effect.",
          ar: "يجوز لنا تعليق وصولك إلى الخدمة أو إنهاؤه إذا أخللت بهذه الشروط أو إذا كنا مُلزَمين بذلك بموجب القانون. ويمكنك التوقّف عن استخدام الخدمة في أي وقت. وتبقى الأحكام التي ينبغي بطبيعتها أن تظل سارية بعد الإنهاء نافذةً.",
        },
      ],
    },
    {
      id: "changes",
      heading: { en: "Changes to these Terms", ar: "التغييرات على هذه الشروط" },
      body: [
        {
          type: "p",
          en: "We may update these Terms from time to time. If we make material changes, we will provide notice by reasonable means. Your continued use of the Service after the changes take effect constitutes acceptance of the updated Terms.",
          ar: "قد نُحدّث هذه الشروط من وقت لآخر. وإذا أجرينا تغييرات جوهرية، فسنقدّم إشعاراً بالوسائل المعقولة. ويُعدّ استمرارك في استخدام الخدمة بعد نفاذ التغييرات قبولاً منك للشروط المحدَّثة.",
        },
      ],
    },
    {
      id: "governing-law",
      heading: { en: "Governing Law & Disputes", ar: "القانون الحاكم والنزاعات" },
      body: [
        {
          type: "p",
          en: "These Terms are governed by the laws of the State of Qatar, without regard to conflict-of-laws principles. Any dispute arising out of or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the competent courts of the State of Qatar.",
          ar: "تخضع هذه الشروط لقوانين دولة قطر، دون اعتبار لمبادئ تنازع القوانين. ويخضع أي نزاع ينشأ عن هذه الشروط أو الخدمة أو يتعلق بهما للاختصاص القضائي الحصري للمحاكم المختصة في دولة قطر.",
        },
      ],
    },
    {
      id: "contact",
      heading: { en: "Contact Us", ar: "تواصل معنا" },
      body: [
        {
          type: "p",
          en: "If you have questions about these Terms, please contact us at info@taqat.qa.",
          ar: "إذا كانت لديك أي أسئلة حول هذه الشروط، يُرجى التواصل معنا عبر info@taqat.qa.",
        },
      ],
    },
  ],
}
